import * as React from "react";
import SearchService, {GeoProps, SearchResult} from "../services/SearchService";
import ResultsList from "./ResultsList";
import ActiveFilters from "./ActiveFilters";
import FilterBucket from "./FilterBucket";
import MapView from "./MapView";
import IsLoading from "./IsLoading";
import Error from "./Error";
import InputRange from "./InputRange";

export type Filters = Map<Filter, Set<FilterValue>>;

interface Props {
	searchService: SearchService;
}
export type viewType = 'list' | 'map' | 'grid';
export type latLongType = [number, number] | null;

interface State {
	results: SearchResult | null | undefined;
	filters: Filters;
	offset: number;
	limit: number;
	seed: number;
    openFilter: null | string;
    view: viewType;
    isLoading: IsLoadingProps;
    geo: GeoProps;
}

interface IsLoadingProps{
    loading: boolean;
    show: boolean
}

export interface Filter {
	title: string;
	id: string;
}

export interface FilterValue {
	value: string;
	label: string;
	checked: boolean;
	count?: number;
}

export interface FilterChangesCallback {
	(value: string, filter: Filter): void;
}

const categoryObj = {
    title: 'Kategorie',
    id: 'category',
};

const locationObj = {
    title: 'Ort',
    id: 'location',
};

const districtObj = {
    title: 'Teilorte',
    id: 'district',
};

class App extends React.Component<Props, State> {
    private timerDistance: any;
    private timerLoading: any;

	constructor(props: Props) {
		super(props);
        const queryParams = new URLSearchParams(window.location.search);
		const filters = new Map<Filter, Set<FilterValue>>();
        this.timerDistance = null;
        this.timerLoading = null;

        this.state = {
			results: null,
			filters: filters,
			offset: Math.max(0, Number(queryParams.get('offset') || 0)),
			limit: Math.min(50, Number(queryParams.get('limit') || 15)),
			seed: Number(localStorage.getItem('monumentfinder_seed') || Math.floor(Math.random() * 100)),
            openFilter: null,
            view: 'grid',
            isLoading: {
                loading: false,
                show: false
            },
            geo: {
                distance: 10
            },
		};

		localStorage.setItem('monumentfinder_seed', this.state.seed.toString());
	}

	async componentDidMount() {

        this.props.searchService.setId(this.state.seed);
		await this.props.searchService.search(this.state.view === "map", new Map<string, Set<string>>(), 0, 0, this.state.geo).then(results => {
            this.updateFilters(results.buckets);

			const queryParams = new URLSearchParams(window.location.search);
			Array.from(this.state.filters.keys()).forEach(filter => {
				if (queryParams.has('filters.'+filter.id)) {
					const checkedFilterValues = queryParams.get('filters.'+filter.id).split(',');
					checkedFilterValues.forEach(checkedValue => {
						this.state.filters.get(filter).forEach(v => {
							if (v.value == checkedValue) {
								v.checked = !v.checked;
							}
						});
					});
				}
			});
			this.setState({
				filters: this.state.filters,
                geo: queryParams.has("geo") ? JSON.parse(queryParams.get("geo")) : { distance: 10 },
                view: queryParams.has("geo") ? 'map' :  "grid"
			});
		});

		for (let offset = 0; offset <= this.state.offset; offset = offset + this.state.limit) {
			await this.doSearch(this.state.view === "map", this.state.filters, offset, this.state.limit, this.state.geo);
		}
	}
    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.timerLoading){
            clearTimeout(this.timerLoading)
        }

        if (this.state.isLoading.loading){
            this.timerLoading = setTimeout(() => {
                this.setState({
                    isLoading: {
                        loading: true,
                        show: true
                    }
                })
            }, 500)
        }
        if (this.state.isLoading.show && !this.state.isLoading.loading ){
            this.setState({
                isLoading: {
                    loading: false,
                    show: false
                }
            })
        }
    }
    handleFilterChange(value: string, filter: Filter) {
        this.state.filters.get(filter).forEach(v => {
			if (v.value == value) {
                if (v.checked && filter.id === 'location'){
                    this.state.filters.get(districtObj).forEach(element => {
                        if (element.checked){
                            element.checked = false;
                        }
                    })
                }
				v.checked = !v.checked;
			}
		});

		this.setState({
			filters: this.state.filters,
			offset: 0, // Reset offset on filter change,
            openFilter: null
		}, () => {
			this.doSearch(this.state.view === "map", this.state.filters, this.state.offset, this.state.limit, this.state.geo);
		});

	}



	handleLoadMore() {
		this.setState({
			offset: this.state.offset + this.state.limit
		}, () => {
			this.doSearch(false, this.state.filters, this.state.offset, this.state.limit, this.state.geo);
		});
	}

	doSearch(withGeo: boolean, filters: Filters, offset: number = 0, limit: number = 15, geo: GeoProps) {
        this.setState({
            isLoading:{
                loading: true,
                show: false
            }
        })
		const queryParams = new URLSearchParams(window.location.search);
		queryParams.set('limit', limit.toString());
		queryParams.set('offset', offset.toString());

		if ( this.state.geo.hasOwnProperty("lat") && this.state.geo.hasOwnProperty("lng")) {
			queryParams.set('geo', JSON.stringify(geo));
		}else{
            queryParams.delete('geo')
        }

		const checkedFilters = this.getCheckedFilters(this.state.filters);
		const filterMap: Map<string, Set<string>> = new Map<string, Set<string>>();

		checkedFilters.forEach((values, filter) => {
			filterMap.set(filter.id, values);

			if (values.size > 0) {
				queryParams.set('filters.' + filter.id, Array.from(values).toString());
			} else if (queryParams.has('filters.' + filter.id)) {
				queryParams.delete('filters.' + filter.id);
			}
		});

		window.history.replaceState(null, null, '?' + queryParams);

		return this.props.searchService.search(withGeo, filterMap, limit, offset, geo).then(results => {
            if (results === undefined){
                this.setState({
                    results: undefined,
                    isLoading: {
                        loading: false,
                        show: false
                    }
                })
                return;
            }
            //geo.lng === undefined && geo.lat === undefined
            if (offset == 0 && results.results && results.results.length > 0) {
				this.updateFilters(results.buckets, filterMap);
			}
			if (offset > 0 && this.state.results != null) {
                this.state.results.results = this.state.results.results.concat(results.results);

				this.setState({
					results: this.state.results
				});

			} else {
				this.setState({
					results: results
				});
			}
            this.setState({
                isLoading: {
                    loading: false,
                    show: false
                }
            })
		});
	}


    private toggleFilterHandler(id: string){

        this.setState({
            openFilter: id === this.state.openFilter ? null : id
        })
    }

	private updateFilters(buckets: any, filterMap?: Map<string, Set<string>>) {
        if (buckets === undefined){
            return;
        }
		const categoryFilterValues: Set<FilterValue> = new Set<FilterValue>();
		const categoryFilterChecked: Set<string> = filterMap && filterMap.has('category') ? filterMap.get('category') : new Set<string>();
		buckets.category.forEach((category: any) => {
            categoryFilterValues.add({
				value: category.label,
				label: category.label,
				checked: categoryFilterChecked.has(category.label),
				count: category.count,
			});
		});

		const locationFilterValues: Set<FilterValue> = new Set<FilterValue>();
		const locationFilterChecked: Set<string> = filterMap && filterMap.has('location') ? filterMap.get('location') : new Set<string>();

		buckets.location.forEach((location: any) => {
            locationFilterValues.add({
				value: location.label,
				label: location.label,
				checked: locationFilterChecked.has(location.label),
				count: location.count,
			});
		});

        const districtFilterValues: Set<FilterValue> = new Set<FilterValue>();
        const districtFilterChecked: Set<string> = filterMap && filterMap.has('district') ? filterMap.get('district') : new Set<string>();
        buckets.district.forEach((district: any) => {
            districtFilterValues.add({
                value: district.label,
                label: district.label,
                checked: districtFilterChecked.has(district.label),
                count: district.count,
            });
        });

		const filters = this.state.filters;

		filters.clear();



        filters.set(categoryObj, categoryFilterValues);
		filters.set(locationObj, locationFilterValues);

        const queryParams = new URLSearchParams(window.location.search);
        if (locationFilterChecked.size > 0 || queryParams.has("filters.location")){
            filters.set(districtObj, districtFilterValues);
        }

		this.setState({
			filters: filters
		});
	}

    private async changeViewHandler (view: viewType) {
        if (this.state.view === view){
            return;
        }
        if (view === "map"){
            this.setState({
                offset: 0,
                limit: 5000
            }, () => {
                this.doSearch(true, this.state.filters, this.state.offset, this.state.limit, this.state.geo);
            })
        }
        if (this.state.view === "map" && view !== "map"){

            this.setState({
                offset: 0,
                limit: 15
            },  () => {
                this.doSearch(false, this.state.filters, this.state.offset, this.state.limit, this.state.geo);
            })
        }
        this.setState({
            view
        });
    }

    private deleteLocationHandler(){
        this.setState({
            geo: {
                distance: 10
            }
        }, () => {
            this.doSearch(this.state.view === 'map', this.state.filters, this.state.offset, this.state.limit, this.state.geo);
        })
    }

    distanceMouseUpHandler(e: any){
        const currentGeo = this.state.geo;
        this.setState({
            geo: {
                ...currentGeo,
                distance:  e.target.value
            }
        },() => {
                this.doSearch(this.state.view === 'map', this.state.filters, this.state.offset, this.state.limit, this.state.geo);
        })
    }

    changeGeoHandler(latlong: latLongType){
        const currentGeo = this.state.geo;
        this.setState({
            geo: {
                ...currentGeo,
                lat: latlong[0],
                lng: latlong[1],
            }

        }, () => {
            this.doSearch(this.state.view === 'map', this.state.filters, this.state.offset, this.state.limit, this.state.geo);
        })
    }

    getLocation() {
            /* geolocation is available */
            navigator.geolocation.getCurrentPosition((position) => {
                const currentGeo = this.state.geo;
                const locationGeo: GeoProps = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                this.setState({
                    view: "map",
                    geo: {
                        ...currentGeo,
                        ...locationGeo
                    },
                    limit: 5000,
                    offset: 0
                }, async () =>{
                    await this.doSearch(this.state.view === 'map', this.state.filters, this.state.offset, this.state.limit, this.state.geo);
                })

            }, (err) => {
                console.log(err)
            });
    }

	private getCheckedFilters(filters: Map<Filter, Set<FilterValue>>): Map<Filter, Set<string>> {
		const filterMap = new Map<Filter, Set<string>>();
		if (filters !== undefined) {
			filters.forEach((values, filter) => {
				const checkedFilters = new Set(Array.from(values).filter(value => {
					return value.checked;
				}).map(value => {
					return value.value;
				}));

				filterMap.set(filter, checkedFilters);
			});
		}
		return filterMap;
	}

	getFilterFragment() {
		const filterBuckets = [];


		for (let value of Array.from(this.state.filters)) {
			filterBuckets.push(this.getFilterBucketFragment(value[0], value[1]));
		}
        const max = "200";
        const min = "0";
        const step = "5";
		return (
            <div className={`filters mb-5`}>
                {filterBuckets}
                {'geolocation' in navigator && <div className={`filters-buckets`}>
                    <button className={`${this.state.openFilter === "vicinity" ? "active ": ''} filters-buckets__button `} onClick={(e) => this.toggleFilterHandler('vicinity')} type={"button"}>Umkreis<span className={'icon-right-arrow'}></span></button>
                    {this.state.openFilter === 'vicinity' && <div className={"filter-bucket"}>
                        <button className={'location-button'} type={'button'} onClick={() => this.getLocation()}  >aktuellen Standort übernehmen</button>
                        {this.state.geo.lng !== undefined && this.state.geo.lat !== undefined && <InputRange
                            id={"distance"} name={"distance-slider"} min={min} max={max}
                            value={this.state.geo.distance} step={step}
                            onMouseUp={this.distanceMouseUpHandler.bind(this)}/>}

                    </div>}
            </div>}
            </div>
		)
	}
	getFilterBucketFragment(filter: Filter, values: Set<FilterValue>) {
        let isDisabled = false;
        values.forEach(value => {
            if(value.checked){
                isDisabled = true
            }
        });
        if (values.size === 0 && filter.id === 'district'){
            return;
        }
        return (<div className={`filters-buckets`}>
            <button disabled={values.size === 0 || isDisabled} id={filter.id} onClick={(e) => this.toggleFilterHandler(filter.id)}
                    type={"button"}
                    className={`${this.state.openFilter === filter.id ? "active " : ''} filters-buckets__button`}>{filter.title}<span
                className={'icon-right-arrow'}></span></button>
            {this.state.openFilter === filter.id &&
                <FilterBucket key={filter.id} onToggleFilterBucket={() => this.toggleFilterHandler(filter.id)}
                              filter={filter} values={values}
                              onFilterChange={(value) => this.handleFilterChange(value, filter)}/>}

        </div>
			);
	}

	getResultSwitch(){
		return (
			<div className={"results-switch"} role="radiogroup" aria-label="Ergebnis-Ansicht wechseln">
				<button className={`${this.state.view === 'grid' ? "active" : ""}`} aria-label="Kachel-Ansicht" role="radio" aria-checked={this.state.view === 'grid' ? "true" : "false"} onClick={() => this.changeViewHandler("grid")}><span className={`icon icon-grid`}></span></button>
				<button className={`${this.state.view === 'list' ? "active" : ""} list-switch`} aria-label="Listen-Ansicht" role="radio" aria-checked={this.state.view === "list" ? "true" : "false"} onClick={() => this.changeViewHandler("list")}><span className={`icon icon-list`}></span></button>
				<button className={`${this.state.view === 'map' ? "active" : ""} `} aria-label="Karten-Ansicht" role="radio" aria-checked={this.state.view === "map" ? "true" : "false"} onClick={() => this.changeViewHandler("map")}><span className={`icon icon-pin`}></span></button>
			</div>
		)
	}

    getView(){
        if (this.state.results === null){
            return ;
        }
        if (this.state.view !== 'map' && this.state.results === undefined || this.state.view !== 'map' && this.state.results.results === undefined){
            return <Error>
                Es ist ein Fehler aufgetreten.<br/>
                Bitte versuchen Sie es erneut.
            </Error>;
        }
        if (this.state.view !== 'map' && this.state.results?.results.length === 0){
            return <Error>
                Es wurden leider keine Kleindenkmale gefunden.<br/>
                Bitte versuchen Sie es erneut.
            </Error>;
        }
        if (this.state.view === 'map'){
            return <MapView onChangeGeo={this.changeGeoHandler.bind(this)} geo={this.state.geo} results={this.state.results.results} />;
        }
        return <ResultsList offset={this.state.offset} view={this.state.view} results={this.state.results.results} meta={this.state.results.meta} onLoadMore={this.handleLoadMore.bind(this)} />
    }

    getHits() {
        if (this.state.results === null || this.state.results === undefined){
            return "0";
        }
        if (this.state.view === 'map'){
            return this.state.results.results?.length ?? "0";
        }
        return this.state.results.meta.hits;
    }

    getStatistics(){
        if (this.state.results === null || this.state.results === undefined || this.state.results.results === undefined){
            return "0 Kleindenkmale";
        }
        if (this.state.view !== 'map' ){
            return <span>{this.state.results.results.length ?? '0'} / {this.getHits()} {this.state.results.meta.hits != 1 ? 'Kleindenkmale' : 'Kleindenkmal'}</span>
        }
        return  <span>{this.getHits()} {this.state.results.results.length != 1 ? 'Kleindenkmale' : 'Kleindenkmal'}</span>
    }


	render() {
        const checkedFilters = this.getCheckedFilters(this.state.filters);
        const hasLatLng =  this.state.geo.lng !== undefined && this.state.geo.lat !== undefined;
		const isFilteredSearch = [].concat.apply([], Array.from(checkedFilters.values()).map(set => Array.from(set))).length > 0 || hasLatLng;
        const activeFilters = (isFilteredSearch) ? <ActiveFilters filters={checkedFilters} geo={this.state.geo} onDeleteLocation={this.deleteLocationHandler.bind(this)} onFilterChange={this.handleFilterChange.bind(this)} /> : null;
		return (

			<div className={"monument-finder"}>

                {this.state.isLoading.show && <IsLoading/>}
                {this.getFilterFragment()}

                {activeFilters}

                <div className="result-switch mb-4">
                    {this.getStatistics()}
                    {this.getResultSwitch()}
                </div>
                <div className={"monument-container"}>
                    {this.getView()}
                </div>

			</div>
		)
	}
}

export default App;
